import React from 'react';
import { Entry } from '../types';
import { formatBytes, formatMilliseconds, getUrlWithoutDomain } from '../utils/formatters';

interface RequestListProps {
  entries: Entry[];
  selectedEntry: Entry | null;
  onSelectEntry: (entry: Entry) => void;
}

const getStatusColor = (status: number): string => {
  if (status >= 500) return 'text-red-500';
  if (status >= 400) return 'text-orange-500';
  if (status >= 300) return 'text-yellow-500';
  if (status >= 200) return 'text-green-500';
  return 'text-gray-500';
};

const RequestItem: React.FC<{ entry: Entry; isSelected: boolean; onSelect: () => void }> = React.memo(({ entry, isSelected, onSelect }) => {
  const { request, response, time } = entry;
  
  // Replace this line
  // const shortUrl = request.url.length > 60 ? `${request.url.substring(0, 57)}...` : request.url;
  
  // With this implementation that removes the domain
  const pathOnly = getUrlWithoutDomain(request.url);
  const shortUrl = pathOnly.length > 60 ? `${pathOnly.substring(0, 57)}...` : pathOnly;
  
  const statusColor = getStatusColor(response.status);

  // Rest of the component remains unchanged
  return (
    <li
      onClick={onSelect}
      className={`p-3 border-b border-gray-200 cursor-pointer transition-all duration-200 ease-in-out
                  ${isSelected ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-500' : 'hover:bg-gray-100 text-gray-800'}`}
    >
      <div className="flex justify-between items-center mb-1">
        <span className={`font-semibold text-sm ${request.method === 'POST' ? 'text-sky-500' : request.method === 'GET' ? 'text-emerald-500' : 'text-amber-500'}`}>
          {request.method}
        </span>
        <span className={`font-semibold text-sm ${statusColor}`}>{response.status} {response.statusText}</span>
      </div>
      <p className="text-sm text-gray-700 truncate" title={request.url}>{shortUrl}</p>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{formatMilliseconds(time)}</span>
        <span>{formatBytes(response.content.size)}</span>
      </div>
    </li>
  );
});
RequestItem.displayName = 'RequestItem';

const RequestList: React.FC<RequestListProps> = ({ entries, selectedEntry, onSelectEntry }) => {
  if (!entries.length) {
    return <p className="text-gray-500 text-center py-4">No requests match your search or no file loaded.</p>;
  }

  return (
    <ul className="ht-requests-list divide-y divide-gray-200">
      {entries.map((entry, index) => (
        <RequestItem
          key={`${entry.startedDateTime}-${index}`} // Using index as fallback for non-unique startedDateTime
          entry={entry}
          isSelected={selectedEntry === entry}
          onSelect={() => onSelectEntry(entry)}
        />
      ))}
    </ul>
  );
};

export default RequestList;